import React, { useEffect } from 'react';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import CoachingConstants from 'coaching/CoachingConstants';
import Divider from 'components/Divider';
import Heading from 'components/Heading';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import List from 'components/List';
import Row from 'components/Row';
import SidebarFooter from 'components/SidebarFooter';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import EventTracking from 'lib/EventTracking';
import {
  userReadableDateTimeFormat,
  extractExceptionDatesToUserReadableStrings,
  userReadableDayAndTimeFormatLocalTZ,
  fullMonthAndDayFormat,
} from 'lib/dateTimeFormat';
import { CoachingEventDefinitionInput, TimeslotFrequencyInput } from 'globalTypes';

export type Props = {
  streamUuid: string;
  eventDefinition?: CoachingEventDefinitionInput;
  eventPrefix?: string;
  frequency?: TimeslotFrequencyInput;
};

export default function BookSessionConfirmationSidebar({
  streamUuid,
  eventDefinition,
  eventPrefix,
  frequency,
}: Props) {
  function trackKey(key: string) {
    eventPrefix && EventTracking.trackKey(`${eventPrefix}.${key}`);
  }

  useEffect(() => {
    trackKey('view');
  }, []);

  const unavailableDates = extractExceptionDatesToUserReadableStrings(
    eventDefinition?.recurrence ?? []
  );
  return (
    <>
      <BoxBody size="xsmall">
        <BookingConfirmationHeader
          startDateTime={eventDefinition?.startDateTime}
          frequency={frequency}
        />
        <Divider />
        {unavailableDates.length > 0 ? (
          <BookingUnavailableDatesSection
            data-test="unavailable-dates-section"
            unavailableDates={unavailableDates}
          />
        ) : (
          <BookingNextStepsSection
            data-test="next-steps-section"
            streamUuid={streamUuid}
            eventKey={`${eventPrefix}.message_coach.click`}
          />
        )}
      </BoxBody>
      <SidebarFooter noShadow>
        <BoxBody size="small">
          <Link weight="semibold" href={LinkConstants.COACHING_HELP_SCHEDULING} target="_blank">
            Review our scheduling policy
          </Link>
        </BoxBody>
      </SidebarFooter>
    </>
  );
}

function getSessionStartDetailsText(frequency?: TimeslotFrequencyInput, startDateTime?: string) {
  if (!frequency) {
    return 'Your session is scheduled';
  }

  if (!startDateTime) {
    return frequency === TimeslotFrequencyInput.once
      ? 'Your session is scheduled'
      : 'Your sessions are scheduled';
  }

  const dayAndTimeString = userReadableDayAndTimeFormatLocalTZ(startDateTime);
  const startDate = fullMonthAndDayFormat(startDateTime);

  switch (frequency) {
    case TimeslotFrequencyInput.weekly:
      return (
        <>
          Your weekly sessions for <Text weight="semibold">every {dayAndTimeString} </Text> will
          start on <Text weight="semibold">{startDate}</Text>
        </>
      );
    case TimeslotFrequencyInput.everyOtherWeek:
      return (
        <>
          Your biweekly sessions for <Text weight="semibold">every other {dayAndTimeString} </Text>
          will start on <Text weight="semibold">{startDate}</Text>
        </>
      );
    default:
      return (
        <>
          Your session is scheduled for{' '}
          <Text weight="semibold">{userReadableDateTimeFormat(startDateTime)}</Text>
        </>
      );
  }
}

function BookingConfirmationHeader({
  startDateTime,
  frequency,
}: {
  startDateTime?: string;
  frequency?: TimeslotFrequencyInput;
}) {
  return (
    <>
      <Heading level="2">You're all set!</Heading>
      <>
        {getSessionStartDetailsText(frequency, startDateTime)}. A confirmation has been emailed to
        you for your records.
      </>
    </>
  );
}

function BookingNextStepsSection({
  streamUuid,
  eventKey,
}: {
  streamUuid: string;
  eventKey?: string;
}) {
  return (
    <>
      <Heading level="4">
        <Text weight="normal">Next steps</Text>
      </Heading>
      <Row size="small">
        Before your session, say hello and share the homework you'd like to go over with your coach.
      </Row>
      <Button
        eventKey={eventKey}
        level="secondary"
        full
        to={`${CoachingConstants.COACHING_CHAT_STREAM_ROUTE_PREFIX}/${streamUuid}`}>
        Message tutor
      </Button>
    </>
  );
}

function BookingUnavailableDatesSection({ unavailableDates }: { unavailableDates: string[] }) {
  return (
    <>
      <Heading level="4">
        <TextWithIcon weight="normal" icon="info-circle" center={false}>
          Note
        </TextWithIcon>
      </Heading>
      <Row size="small">
        <Box noBorder noShadow textAlign="left" color="paper">
          <BoxBody size="xsmall">
            <Row size="xsmall">Your coach is unavailable for the following sessions:</Row>
            <List delimiter="bullet" markerPosition="inside">
              {unavailableDates.map(d => (
                <li key={d}>{d}</li>
              ))}
            </List>
          </BoxBody>
        </Box>
      </Row>
      You may reschedule or work with another coach for these sessions.
    </>
  );
}
